import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, Button, Stack, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Referral, ReferralStage, ReferralStatus, DecisionOutcome,
  IngestionType, ExecutionStep, ReprocessingTask,
} from '~/types/transition';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';

interface ReferralStageActionCardProps {
  referral: Referral;
}

// ── Stage color config (matches Predict's WorkflowStageCard palette) ──────────

type CardVariant = 'intake_waiting' | 'intake_auto' | 'processing' | 'awaiting_info' | 'decision' | 'accepted' | 'declined' | 'auto_declined';

function getVariant(referral: Referral): CardVariant {
  if (referral.autoDeclined) return 'auto_declined';
  const { stage, status, ingestionType } = referral;
  if (stage === ReferralStage.INTAKE) {
    return ingestionType === IngestionType.ASSISTED ? 'intake_waiting' : 'intake_auto';
  }
  if (stage === ReferralStage.PROCESSING) {
    return status === ReferralStatus.AWAITING_INFO ? 'awaiting_info' : 'processing';
  }
  if (stage === ReferralStage.DECISION) return 'decision';
  const isDecline =
    referral.finalDecision === DecisionOutcome.DECLINE ||
    referral.finalDecision === DecisionOutcome.OVERRIDE_DECLINE;
  return isDecline ? 'declined' : 'accepted';
}

const COLORS: Record<CardVariant, { bg: string; chip: string; stageLabel: string }> = {
  intake_waiting: { bg: '#E3F2FD', chip: '#0D47A1', stageLabel: 'Intake' },
  intake_auto:    { bg: '#E3F2FD', chip: '#0D47A1', stageLabel: 'Intake' },
  processing:     { bg: '#E3F2FD', chip: '#1565c0', stageLabel: 'Processing' },
  awaiting_info:  { bg: '#FFF3E0', chip: '#E65100', stageLabel: 'Processing' },
  decision:       { bg: '#F3E5F5', chip: '#6a1b9a', stageLabel: 'Decision' },
  accepted:       { bg: '#E8F5E9', chip: '#1B5E20', stageLabel: 'Execution' },
  declined:       { bg: '#FFEBEE', chip: '#B71C1C', stageLabel: 'Execution' },
  auto_declined:  { bg: '#FBE9E7', chip: '#BF360C', stageLabel: 'Auto-Declined' },
};

function getStatusTitle(referral: Referral, variant: CardVariant): string {
  switch (variant) {
    case 'intake_waiting': return 'Awaiting Processing';
    case 'intake_auto':    return 'Auto-Ingestion in Progress';
    case 'processing':     return 'AI Processing in Progress';
    case 'awaiting_info':  return 'Missing Information — Awaiting Response';
    case 'decision':       return 'AI Recommendation Ready';
    case 'auto_declined':
      return referral.outreachStatus === 'SENT'
        ? 'Auto-Declined — Outreach Sent'
        : 'Auto-Declined — Outreach Pending';
    case 'accepted':
    case 'declined': {
      const base =
        referral.finalDecision === DecisionOutcome.OVERRIDE_ACCEPT ? 'Accepted (Override)' :
        referral.finalDecision === DecisionOutcome.OVERRIDE_DECLINE ? 'Declined (Override)' :
        variant === 'accepted' ? 'Accepted' : 'Declined';
      const parts = [base];
      if (referral.finalDecisionBy) parts.push(referral.finalDecisionBy);
      if (referral.finalDecisionAt) parts.push(formatTimestamp(referral.finalDecisionAt));
      return parts.join(' · ');
    }
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const AiChip: React.FC<{ outcome: DecisionOutcome }> = ({ outcome }) => {
  if (outcome === DecisionOutcome.ACCEPT)
    return <Chip icon={<CheckCircleIcon />} label="Accept" color="success" size="small" sx={{ fontWeight: 600 }} />;
  if (outcome === DecisionOutcome.DECLINE)
    return <Chip icon={<CancelIcon />} label="Decline" color="error" size="small" sx={{ fontWeight: 600 }} />;
  return <Chip icon={<PendingIcon />} label="Hold — awaiting review" size="small" sx={{ fontWeight: 600 }} />;
};

function interpretIssue(note: string): Array<{ action: string; detail: string }> {
  const tasks: Array<{ action: string; detail: string }> = [];
  if (/insurance|member.?id|coverage|payer|medicaid|medicare/i.test(note)) {
    tasks.push({
      action: 'Verifying insurance coverage and member ID',
      detail: 'Contacting the insurance provider to confirm active coverage and validate the member ID on file.',
    });
  }
  if (/diagnosis|icd|code|condition/i.test(note)) {
    tasks.push({
      action: 'Re-extracting and verifying diagnosis information',
      detail: 'Re-reading clinical documentation and verifying the ICD-10 code against program eligibility criteria.',
    });
  }
  if (/address|location|zip|city|service.?area|territory/i.test(note)) {
    tasks.push({
      action: 'Re-verifying patient address and service area eligibility',
      detail: 'Re-checking patient address against active service area boundaries and updating territory assignment.',
    });
  }
  if (/phone|call|contact|reach|referring|provider|facility/i.test(note)) {
    tasks.push({
      action: 'Contacting referring provider to clarify information',
      detail: "Calling the referring provider's office to clarify the noted discrepancy and update the record.",
    });
  }
  if (/program|eligib|qualify|criteria/i.test(note)) {
    tasks.push({
      action: 'Re-evaluating program eligibility',
      detail: "Re-assessing the patient's clinical profile against program admission criteria.",
    });
  }
  if (/document|attach|pdf|fax|page|illegible|missing/i.test(note)) {
    tasks.push({
      action: 'Requesting updated or supplemental documentation',
      detail: 'Contacting the referring facility to request corrected or additional documentation.',
    });
  }
  if (tasks.length === 0) {
    tasks.push({
      action: 'Re-processing with noted issue flagged for review',
      detail: "Re-analyzing the referral with the coordinator's note attached as a targeted review flag.",
    });
  }
  return tasks;
}

const StepIcon: React.FC<{ status: ExecutionStep['status'] }> = ({ status }) => {
  switch (status) {
    case 'COMPLETE':     return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    case 'IN_PROGRESS':  return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
    case 'FAILED':       return <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
    default:             return <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

const ReferralStageActionCard: React.FC<ReferralStageActionCardProps> = ({ referral }) => {
  const {
    beginProcessing, returnToProcessing, simulateReprocessingComplete,
    acceptReferral, declineReferral,
    overrideDecision, simulateMissingInfoResponse,
    overrideAutoDecline, simulateOutreachSent,
  } = useTransitionContext();

  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnStep, setReturnStep] = useState<'describe' | 'interpreting' | 'confirm'>('describe');
  const [returnNote, setReturnNote] = useState('');
  const [proposedTasks, setProposedTasks] = useState<Array<{ action: string; detail: string }>>([]);
  const [acceptReason, setAcceptReason] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [overrideAutoDeclineOpen, setOverrideAutoDeclineOpen] = useState(false);
  const [overrideAutoDeclineReason, setOverrideAutoDeclineReason] = useState('');

  const handleReturnInterpret = () => {
    setReturnStep('interpreting');
    setTimeout(() => {
      setProposedTasks(interpretIssue(returnNote));
      setReturnStep('confirm');
    }, 1600);
  };

  const handleReturnConfirm = () => {
    const tasks: ReprocessingTask[] = proposedTasks.map((t, i) => ({
      id: `rp-${i}`,
      action: t.action,
      detail: t.detail,
      status: 'IN_PROGRESS',
    }));
    returnToProcessing(referral.id, returnNote, tasks);
    setReturnDialogOpen(false);
    setReturnNote('');
    setReturnStep('describe');
    setProposedTasks([]);
  };

  const closeReturnDialog = () => {
    setReturnDialogOpen(false);
    setReturnNote('');
    setReturnStep('describe');
    setProposedTasks([]);
  };

  const variant = getVariant(referral);
  const colors = COLORS[variant];
  const statusTitle = getStatusTitle(referral, variant);

  const aiSaysAccept = referral.aiRecommendation === DecisionOutcome.ACCEPT;
  const aiSaysDecline = referral.aiRecommendation === DecisionOutcome.DECLINE;
  const acceptNeedsOverride = !aiSaysAccept;
  const declineNeedsOverride = aiSaysAccept;

  const handleAcceptClick = () => {
    if (acceptNeedsOverride) setAcceptDialogOpen(true);
    else acceptReferral(referral.id);
  };

  const handleAcceptSubmit = () => {
    if (!acceptReason.trim()) return;
    overrideDecision(referral.id, DecisionOutcome.OVERRIDE_ACCEPT, acceptReason);
    setAcceptDialogOpen(false);
    setAcceptReason('');
  };

  const handleDeclineSubmit = () => {
    if (!declineReason.trim()) return;
    if (declineNeedsOverride) overrideDecision(referral.id, DecisionOutcome.OVERRIDE_DECLINE, declineReason);
    else declineReferral(referral.id, declineReason);
    setDeclineDialogOpen(false);
    setDeclineReason('');
  };

  const steps = referral.executionSteps ?? [];
  const completeCount = steps.filter((s) => s.status === 'COMPLETE').length;
  const allStepsComplete = steps.length > 0 && completeCount === steps.length;

  const renderBody = () => {
    switch (variant) {
      case 'intake_waiting':
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
              This {referral.source.toLowerCase()} referral requires a coordinator to begin AI extraction.
              Patient data has not yet been processed.
            </Typography>
            <Button
              variant="contained" size="large" startIcon={<PlayArrowIcon />}
              onClick={() => beginProcessing(referral.id)} fullWidth
              sx={{ py: 1.5, bgcolor: colors.chip, '&:hover': { bgcolor: colors.chip, filter: 'brightness(0.9)' } }}
            >
              Begin Processing
            </Button>
          </>
        );

      case 'intake_auto':
        return (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            AI extraction will begin automatically. No coordinator action required for{' '}
            {referral.source.toLowerCase()} referrals.
          </Typography>
        );

      case 'processing': {
        const tasks = referral.reprocessingTasks ?? [];
        if (tasks.length > 0) {
          const allDone = tasks.every((t) => t.status === 'COMPLETE');
          return (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: 'warning.dark', display: 'block', mb: 0.5 }}>
                  Returned for Review
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: '0.82rem', lineHeight: 1.5 }}>
                  "{referral.reprocessingNote}"
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: 'text.disabled', display: 'block', mb: 1 }}>
                AI Re-processing Actions
              </Typography>
              <Stack spacing={1.25} sx={{ mb: 2.5 }}>
                {tasks.map((task) => (
                  <Box key={task.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <Box sx={{ mt: 0.15, flexShrink: 0 }}>
                      {task.status === 'COMPLETE'
                        ? <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
                        : <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                        {task.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {task.status === 'COMPLETE' && task.completedAt
                          ? `Completed ${formatTimestamp(task.completedAt)}`
                          : task.detail}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
              {!allDone && (
                <Button
                  variant="contained" size="large" startIcon={<CheckCircleIcon />} color="success"
                  onClick={() => simulateReprocessingComplete(referral.id)} fullWidth sx={{ py: 1.5 }}
                >
                  Simulate Re-processing Complete
                </Button>
              )}
            </>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            AI is extracting and validating referral data. This referral will advance to the Decision
            stage once processing is complete.
          </Typography>
        );
      }

      case 'awaiting_info': {
        const unresolved = referral.missingFields.filter((f) => !f.resolved);
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              Processing is paused. The following information could not be extracted from the document.
              This referral cannot advance until all required items are resolved.
            </Typography>

            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: 'warning.dark', display: 'block', mb: 1 }}>
              Missing Information
            </Typography>

            <Stack spacing={1.25} sx={{ mb: 2.5 }}>
              {unresolved.map((f) => {
                const actionText = f.aiOutreachSent && f.outreachTimestamp
                  ? `AI called referring provider at ${formatTimestamp(f.outreachTimestamp)} — voicemail left, awaiting callback`
                  : f.aiOutreachSent
                  ? 'AI called referring provider — voicemail left, awaiting callback'
                  : 'Manual outreach required — no phone number on file';
                return (
                  <Box key={f.field} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <WarningAmberIcon sx={{ fontSize: '0.95rem', color: 'warning.main', flexShrink: 0, mt: 0.2 }} />
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      <Box component="span" fontWeight={600}>{f.label}</Box>
                      {' '}
                      <Chip
                        label={f.required ? 'Required' : 'Optional'}
                        size="small"
                        color={f.required ? 'error' : 'default'}
                        variant="outlined"
                        sx={{ height: 16, fontSize: '0.6rem', verticalAlign: 'middle', mr: 0.5, '& .MuiChip-label': { px: 0.75 } }}
                      />
                      {' — '}
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                        {actionText}
                      </Box>
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
            <Button
              variant="contained" size="large" startIcon={<CheckCircleIcon />} color="success"
              onClick={() => simulateMissingInfoResponse(referral.id)} fullWidth sx={{ py: 1.5 }}
            >
              Simulate Response Received
            </Button>
          </>
        );
      }

      case 'auto_declined': {
        const outreachSent = referral.outreachStatus === 'SENT';
        return (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
              {referral.autoDeclineReason}
            </Typography>

            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: outreachSent ? 'error.main' : 'warning.dark', display: 'block', mb: 0.75 }}>
              {outreachSent ? 'Outreach Sent to Referring Provider' : 'Outreach Pending — Review Window Open'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.82rem' }}>
              {outreachSent
                ? `Decline notification sent ${referral.outreachSentAt ? formatTimestamp(referral.outreachSentAt) : ''}. Overriding now will send a correction notice to the referring provider.`
                : `Outreach is scheduled for ${referral.outreachScheduledAt ? formatTimestamp(referral.outreachScheduledAt) : 'later'}. Overriding before it fires avoids sending a decline notice.`}
            </Typography>

            <Stack spacing={1}>
              <Button
                variant="contained" size="large" startIcon={<CheckCircleIcon />}
                color={outreachSent ? 'warning' : 'success'}
                onClick={() => setOverrideAutoDeclineOpen(true)} fullWidth sx={{ py: 1.5 }}
              >
                {outreachSent ? 'Override — Accept + Send Correction' : 'Override — Accept Referral'}
              </Button>
              {!outreachSent && (
                <Button
                  variant="outlined" size="large" startIcon={<ScheduleIcon />}
                  onClick={() => simulateOutreachSent(referral.id)} fullWidth sx={{ py: 1.5 }}
                >
                  Simulate Outreach Fired
                </Button>
              )}
            </Stack>
          </>
        );
      }

      case 'decision':
        return null; // handled by renderDecisionLayout()

      case 'accepted':
      case 'declined': {
        const isAccepted = variant === 'accepted';
        return (
          <>
            {referral.overrideReason && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                Reason: {referral.overrideReason}
              </Typography>
            )}
            {steps.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Execution Steps</Typography>
                  <Chip
                    label={allStepsComplete ? 'Complete' : `${completeCount} / ${steps.length}`}
                    size="small" color={allStepsComplete ? 'success' : 'info'}
                    sx={{ fontSize: '0.65rem', height: 18 }}
                  />
                </Box>
                {!allStepsComplete && (
                  <LinearProgress
                    variant="determinate" value={(completeCount / steps.length) * 100}
                    color={isAccepted ? 'success' : 'error'} sx={{ mb: 2, borderRadius: 1 }}
                  />
                )}
                <Stack spacing={1.25}>
                  {steps.map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ mt: 0.15, flexShrink: 0 }}><StepIcon status={step.status} /></Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: step.status === 'IN_PROGRESS' ? 600 : 400, color: step.status === 'PENDING' ? 'text.disabled' : 'text.primary' }}
                        >
                          {step.label}
                        </Typography>
                        {step.status === 'COMPLETE' && step.timestamp && (
                          <Typography variant="caption" color="text.disabled">{formatTimestamp(step.timestamp)}</Typography>
                        )}
                        {step.status === 'IN_PROGRESS' && (
                          <Typography variant="caption" color="info.main">In progress...</Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </>
        );
      }
    }
  };

  const renderDecisionLayout = () => {
    const RESULT_ICON: Record<string, { icon: React.ReactNode; color: string }> = {
      PASS:    { icon: <CheckCircleIcon fontSize="small" />,   color: '#2e7d32' },
      FAIL:    { icon: <CancelIcon fontSize="small" />,        color: '#c62828' },
      WARN:    { icon: <WarningAmberIcon fontSize="small" />,  color: '#e65100' },
      UNKNOWN: { icon: <HelpOutlineIcon fontSize="small" />,   color: '#9e9e9e' },
    };
    const capacityOk = referral.capacityInfo.available && referral.capacityInfo.availableCount > 0;
    const capacityLabel = capacityOk
      ? `${referral.capacityInfo.territory}: ${referral.capacityInfo.availableCount} slot${referral.capacityInfo.availableCount === 1 ? '' : 's'} available`
      : `${referral.capacityInfo.territory}: no available slots`;

    return (
      <>
        {/* 2-col top row: left = title/stage/rec, right = action buttons */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Left column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
              <Typography variant="h6" fontWeight={600}>Workflow</Typography>
              <Chip
                label={colors.stageLabel}
                size="small"
                sx={{ bgcolor: colors.chip, color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
              />
            </Box>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: 'grey.500', display: 'block', mb: 0.5 }}>
              AI Recommendation
            </Typography>
            <AiChip outcome={referral.aiRecommendation} />
          </Box>

          {/* Right column: stacked action buttons */}
          <Stack spacing={1} sx={{ minWidth: 190, flexShrink: 0 }}>
            <Button
              variant={aiSaysAccept ? 'contained' : 'outlined'} color="success" fullWidth
              startIcon={<CheckCircleIcon />} onClick={handleAcceptClick}
              sx={aiSaysAccept ? { bgcolor: '#1B5E20', '&:hover': { bgcolor: '#1B5E20', filter: 'brightness(0.9)' } } : {}}
            >
              {acceptNeedsOverride ? 'Override — Accept' : 'Accept Referral'}
            </Button>
            <Button
              variant={aiSaysDecline ? 'contained' : 'outlined'} color="error" fullWidth
              startIcon={<CancelIcon />} onClick={() => setDeclineDialogOpen(true)}
            >
              {declineNeedsOverride ? 'Override — Decline' : 'Decline Referral'}
            </Button>
            <Button
              variant="outlined" color="inherit" fullWidth
              startIcon={<ReplayIcon />} onClick={() => setReturnDialogOpen(true)}
              sx={{ borderColor: 'grey.400', color: 'text.secondary' }}
            >
              Return for Review
            </Button>
          </Stack>
        </Box>

        {/* Full-width AI recommendation reason */}
        {referral.aiRecommendationReason && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, lineHeight: 1.6 }}>
            {referral.aiRecommendationReason}
          </Typography>
        )}

        {/* Divider + criteria checklist */}
        {referral.decisionFactors.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
              {referral.decisionFactors.map((factor) => {
                const cfg = RESULT_ICON[factor.result] ?? RESULT_ICON.UNKNOWN;
                return (
                  <Box key={factor.criterion} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ color: cfg.color, flexShrink: 0, display: 'flex' }}>{cfg.icon}</Box>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                      <Box component="span" fontWeight={600}>{factor.criterion}</Box>
                      {' — '}{factor.patientValue}
                    </Typography>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ color: capacityOk ? '#2e7d32' : '#e65100', flexShrink: 0, display: 'flex' }}>
                  {capacityOk ? <CheckCircleIcon fontSize="small" /> : <WarningAmberIcon fontSize="small" />}
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                  <Box component="span" fontWeight={600}>Capacity</Box>
                  {' — '}{capacityLabel}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <Card sx={{ bgcolor: colors.bg }} variant="outlined">
        <CardContent sx={{ p: 2.5 }}>
          {variant === 'decision' ? renderDecisionLayout() : (
            <>
              {/* Fixed card header: title + stage chip, consistent across all other variants */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" fontWeight={600}>Workflow</Typography>
                <Chip
                  label={colors.stageLabel}
                  size="small"
                  sx={{ bgcolor: colors.chip, color: '#fff', fontWeight: 600, fontSize: '0.72rem' }}
                />
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>
                {statusTitle}
              </Typography>
              {renderBody()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Override Accept dialog */}
      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Override — Accept Referral</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
            {aiSaysDecline
              ? 'The AI recommended declining. A reason is required to override.'
              : 'The AI has not issued a clear recommendation. A reason is required to accept.'}
          </Alert>
          <TextField
            label="Override reason" multiline rows={3} fullWidth autoFocus
            value={acceptReason} onChange={(e) => setAcceptReason(e.target.value)}
            placeholder="Enter your reason for accepting..." size="small" sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAcceptDialogOpen(false); setAcceptReason(''); }}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleAcceptSubmit} disabled={!acceptReason.trim()}>
            Confirm Accept
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-decline override dialog */}
      <Dialog open={overrideAutoDeclineOpen} onClose={() => setOverrideAutoDeclineOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Override Auto-Decline</DialogTitle>
        <DialogContent>
          <Alert severity={referral.outreachStatus === 'SENT' ? 'error' : 'warning'} sx={{ mb: 2, fontSize: '0.8rem' }}>
            {referral.outreachStatus === 'SENT'
              ? 'A decline notice has already been sent. Accepting will trigger a correction notice to the referring provider.'
              : 'This referral was automatically declined — patient is outside the active service area. A reason is required to override.'}
          </Alert>
          <TextField
            label="Override reason" multiline rows={3} fullWidth autoFocus
            value={overrideAutoDeclineReason} onChange={(e) => setOverrideAutoDeclineReason(e.target.value)}
            placeholder="e.g. One-time exception approved by clinical director..." size="small" sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOverrideAutoDeclineOpen(false); setOverrideAutoDeclineReason(''); }}>Cancel</Button>
          <Button
            variant="contained" color="success"
            disabled={!overrideAutoDeclineReason.trim()}
            onClick={() => {
              overrideAutoDecline(referral.id, overrideAutoDeclineReason);
              setOverrideAutoDeclineOpen(false);
              setOverrideAutoDeclineReason('');
            }}
          >
            Confirm Override
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={declineDialogOpen} onClose={() => setDeclineDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{declineNeedsOverride ? 'Override — Decline Referral' : 'Decline Referral'}</DialogTitle>
        <DialogContent>
          {declineNeedsOverride && (
            <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
              The AI recommended accepting. A reason is required to override.
            </Alert>
          )}
          <TextField
            label="Reason" multiline rows={3} fullWidth autoFocus
            value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
            placeholder="Enter reason for declining..." size="small"
            sx={{ mt: declineNeedsOverride ? 0.5 : 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeclineDialogOpen(false); setDeclineReason(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeclineSubmit} disabled={!declineReason.trim()}>
            Confirm Decline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return for Review dialog — 3-step AI interaction */}
      <Dialog open={returnDialogOpen} onClose={returnStep !== 'interpreting' ? closeReturnDialog : undefined} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} />
            <Typography variant="h6" fontWeight={600}>Return for Review</Typography>
          </Stack>
        </DialogTitle>

        {/* Step 1: Describe */}
        {returnStep === 'describe' && (
          <>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
                Describe what you noticed. The AI will interpret the issue and propose specific
                actions before sending this referral back to Processing.
              </Typography>
              <TextField
                label="What did you notice?"
                multiline rows={4} fullWidth autoFocus
                value={returnNote} onChange={(e) => setReturnNote(e.target.value)}
                placeholder="e.g. The diagnosis code doesn't match the requested program — ICD-10 S72.001A is a fracture code but the program requested is COPD management."
                size="small"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeReturnDialog} color="inherit">Cancel</Button>
              <Button
                variant="contained"
                disabled={!returnNote.trim()}
                onClick={handleReturnInterpret}
                endIcon={<AutoAwesomeIcon fontSize="small" />}
              >
                Interpret with AI
              </Button>
            </DialogActions>
          </>
        )}

        {/* Step 2: Interpreting */}
        {returnStep === 'interpreting' && (
          <DialogContent sx={{ py: 5, textAlign: 'center' }}>
            <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
              Analyzing the issue...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              AI is interpreting your note and determining what actions to take
            </Typography>
            <LinearProgress sx={{ borderRadius: 1, mx: 4 }} />
          </DialogContent>
        )}

        {/* Step 3: Confirm proposed actions */}
        {returnStep === 'confirm' && (
          <>
            <DialogContent>
              <Box sx={{ mb: 2.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'grey.400' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.25 }}>
                  YOUR NOTE
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                  "{returnNote}"
                </Typography>
              </Box>

              <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.7rem', color: 'primary.main', display: 'block', mb: 1.25 }}>
                Actions that will begin immediately
              </Typography>

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                {proposedTasks.map((task, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <AutoAwesomeIcon sx={{ fontSize: '1rem', color: 'primary.main', mt: 0.15, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                        {task.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {task.detail}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                The AI recommendation will be cleared and re-evaluated once all actions are complete.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReturnStep('describe')} startIcon={<ArrowBackIcon fontSize="small" />} color="inherit">
                Edit
              </Button>
              <Button variant="contained" color="warning" onClick={handleReturnConfirm}>
                Confirm &amp; Return for Review
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ReferralStageActionCard;
